// src/pages/trading-portal/MarketDataIngestion/DataQualityPage.js
// Data quality & storage configuration page

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    TextField,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Switch,
    FormControlLabel,
    Tooltip,
} from '@mui/material';
import {
    VerifiedUser as VerifiedUserIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PlayArrow as PlayArrowIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    DataQualityConfigType,
    DataQualityConfigTypeLabels,
    DataQualityConfigTypeColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const DEFAULT_SETTINGS = {
    BAD_TICK_FILTER: {
        std_deviation_threshold: 3,
        cross_validate_bid_ask: true,
    },
    GAP_FILL: {
        method: 'linear',
        max_gap_size_minutes: 5,
        flag_large_gaps: true,
    },
    RETENTION_POLICY: {
        tick_data_retention: '90_days',
        daily_data_retention: '10_years',
        macro_data_retention: '20_years',
        correlation_data_retention: 'permanent',
    },
    REPLAY_ENGINE: {
        replay_speed: '1x',
        include_macro_snapshot: true,
        include_options_chain: false,
    },
};

const PRESET_TEMPLATES = [
    {
        config_type: 'BAD_TICK_FILTER',
        name: 'Bad Tick Filter',
        description: 'Remove erroneous quotes using statistical bounds checking',
        color: '#ef4444',
    },
    {
        config_type: 'GAP_FILL',
        name: 'Gap Fill',
        description: 'Handle missing data with linear interpolation',
        color: '#3b82f6',
    },
    {
        config_type: 'RETENTION_POLICY',
        name: 'Retention Policy',
        description: 'Default retention: Tick 90d, Daily 10y, Macro 20y, Correlation permanent',
        color: '#f59e0b',
    },
    {
        config_type: 'REPLAY_ENGINE',
        name: 'Replay Engine',
        description: 'Reconstruct historical market state for backtesting',
        color: '#6366f1',
    },
];

const DataQualityPage = () => {
    // Permission checks
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // State
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Dialog states
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [runningId, setRunningId] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        config_type: '',
        is_active: true,
        settings: {},
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-mdi-data-quality', 'view');
            const add = await hasPermission('tr-mdi-data-quality', 'add');
            const edit = await hasPermission('tr-mdi-data-quality', 'edit');
            const del = await hasPermission('tr-mdi-data-quality', 'delete');
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPermissions();
    }, []);

    // Fetch configs
    const fetchConfigs = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.get('/api/market-data-ingestion/data-quality/configs');

            if (response.data.success) {
                setConfigs(response.data.data || []);
            } else {
                setError(response.data.message || 'Failed to fetch configurations');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch configurations');
        } finally {
            setLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (canView) fetchConfigs();
    }, [fetchConfigs, canView]);

    // Form helpers
    const resetForm = () => {
        setFormData({ name: '', config_type: '', is_active: true, settings: {} });
        setFormErrors({});
        setIsEditing(false);
        setSelectedConfig(null);
    };

    const handleCreateOpen = (presetType = null) => {
        resetForm();
        if (presetType) {
            setFormData({
                name: DataQualityConfigTypeLabels[presetType] || '',
                config_type: presetType,
                is_active: true,
                settings: { ...DEFAULT_SETTINGS[presetType] },
            });
        }
        setFormDialogOpen(true);
    };

    const handleEditOpen = (config) => {
        setSelectedConfig(config);
        setIsEditing(true);
        setFormData({
            name: config.name || '',
            config_type: config.config_type || '',
            is_active: config.is_active !== false,
            settings: config.settings && typeof config.settings === 'object' ? { ...config.settings } : { ...DEFAULT_SETTINGS[config.config_type] || {} },
        });
        setFormErrors({});
        setFormDialogOpen(true);
    };

    const handleFormClose = () => {
        setFormDialogOpen(false);
        resetForm();
    };

    const handleFormChange = (field) => (event) => {
        setFormData(prev => ({ ...prev, [field]: event.target.value }));
        if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleSettingsChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            settings: { ...prev.settings, [key]: value },
        }));
    };

    const handleConfigTypeChange = (event) => {
        const newType = event.target.value;
        setFormData(prev => ({
            ...prev,
            config_type: newType,
            settings: { ...DEFAULT_SETTINGS[newType] || {} },
        }));
        if (formErrors.config_type) setFormErrors(prev => ({ ...prev, config_type: null }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = 'Name is required';
        if (!formData.config_type) errors.config_type = 'Config type is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleFormSubmit = async () => {
        if (!validateForm()) return;
        setSubmitting(true);

        try {
            const payload = {
                name: formData.name,
                config_type: formData.config_type,
                is_active: formData.is_active,
                settings: formData.settings,
            };

            let response;
            if (isEditing && selectedConfig) {
                response = await axiosInstance.put(`/api/market-data-ingestion/data-quality/configs/${selectedConfig.id}`, payload);
            } else {
                response = await axiosInstance.post('/api/market-data-ingestion/data-quality/configs', payload);
            }

            if (response.data.success) {
                handleFormClose();
                fetchConfigs();
            } else {
                setFormErrors({ submit: response.data.message || `Failed to ${isEditing ? 'update' : 'create'} configuration` });
            }
        } catch (err) {
            setFormErrors({ submit: err.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} configuration` });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (config) => {
        setSelectedConfig(config);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedConfig) return;
        try {
            const response = await axiosInstance.delete(`/api/market-data-ingestion/data-quality/configs/${selectedConfig.id}`);
            if (response.data.success) {
                fetchConfigs();
                setDeleteDialogOpen(false);
                setSelectedConfig(null);
            } else {
                setError(response.data.message || 'Failed to delete configuration');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete configuration');
        }
    };

    const handleRunCheck = async (config) => {
        setRunningId(config.id);
        setSuccessMessage(null);
        try {
            const response = await axiosInstance.post(`/api/market-data-ingestion/data-quality/run/${config.id}`);
            if (response.data.success) {
                setSuccessMessage(response.data.message || `Check "${config.name}" completed successfully.`);
                fetchConfigs();
            } else {
                setError(response.data.message || 'Failed to run check');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to run check');
        } finally {
            setRunningId(null);
        }
    };

    // Render settings fields based on config type
    const renderSettingsFields = () => {
        const { config_type, settings } = formData;

        if (!config_type) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily, mt: 1 }}>
                    Select a config type to configure settings.
                </Typography>
            );
        }

        switch (config_type) {
            case 'BAD_TICK_FILTER':
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Std Deviation Threshold"
                                type="number"
                                value={settings.std_deviation_threshold ?? 3}
                                onChange={(e) => handleSettingsChange('std_deviation_threshold', parseFloat(e.target.value))}
                                inputProps={{ min: 1, step: 0.5 }}
                                helperText="Number of standard deviations for outlier detection"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.cross_validate_bid_ask !== false}
                                        onChange={(e) => handleSettingsChange('cross_validate_bid_ask', e.target.checked)}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                                    />
                                }
                                label="Cross-validate bid/ask"
                                sx={{ mt: 1 }}
                            />
                        </Grid>
                    </Grid>
                );

            case 'GAP_FILL':
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Method</InputLabel>
                                <Select
                                    value={settings.method || 'linear'}
                                    label="Method"
                                    onChange={(e) => handleSettingsChange('method', e.target.value)}
                                >
                                    <MenuItem value="linear">Linear</MenuItem>
                                    <MenuItem value="forward_fill">Forward Fill</MenuItem>
                                    <MenuItem value="backward_fill">Backward Fill</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Max Gap Size (minutes)"
                                type="number"
                                value={settings.max_gap_size_minutes ?? 5}
                                onChange={(e) => handleSettingsChange('max_gap_size_minutes', parseInt(e.target.value, 10))}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.flag_large_gaps !== false}
                                        onChange={(e) => handleSettingsChange('flag_large_gaps', e.target.checked)}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                                    />
                                }
                                label="Flag Large Gaps"
                            />
                        </Grid>
                    </Grid>
                );

            case 'RETENTION_POLICY':
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Tick Data Retention</InputLabel>
                                <Select
                                    value={settings.tick_data_retention || '90_days'}
                                    label="Tick Data Retention"
                                    onChange={(e) => handleSettingsChange('tick_data_retention', e.target.value)}
                                >
                                    <MenuItem value="90_days">90 Days</MenuItem>
                                    <MenuItem value="1_year">1 Year</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Daily Data Retention</InputLabel>
                                <Select
                                    value={settings.daily_data_retention || '5_years'}
                                    label="Daily Data Retention"
                                    onChange={(e) => handleSettingsChange('daily_data_retention', e.target.value)}
                                >
                                    <MenuItem value="5_years">5 Years</MenuItem>
                                    <MenuItem value="10_years">10 Years</MenuItem>
                                    <MenuItem value="permanent">Permanent</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Macro Data Retention</InputLabel>
                                <Select
                                    value={settings.macro_data_retention || '10_years'}
                                    label="Macro Data Retention"
                                    onChange={(e) => handleSettingsChange('macro_data_retention', e.target.value)}
                                >
                                    <MenuItem value="10_years">10 Years</MenuItem>
                                    <MenuItem value="20_years">20 Years</MenuItem>
                                    <MenuItem value="permanent">Permanent</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Correlation Data Retention</InputLabel>
                                <Select
                                    value={settings.correlation_data_retention || 'permanent'}
                                    label="Correlation Data Retention"
                                    onChange={(e) => handleSettingsChange('correlation_data_retention', e.target.value)}
                                >
                                    <MenuItem value="permanent">Permanent</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                );

            case 'REPLAY_ENGINE':
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Replay Speed</InputLabel>
                                <Select
                                    value={settings.replay_speed || '1x'}
                                    label="Replay Speed"
                                    onChange={(e) => handleSettingsChange('replay_speed', e.target.value)}
                                >
                                    <MenuItem value="1x">1x</MenuItem>
                                    <MenuItem value="2x">2x</MenuItem>
                                    <MenuItem value="5x">5x</MenuItem>
                                    <MenuItem value="10x">10x</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.include_macro_snapshot !== false}
                                        onChange={(e) => handleSettingsChange('include_macro_snapshot', e.target.checked)}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                                    />
                                }
                                label="Include Macro Snapshot"
                                sx={{ mt: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.include_options_chain === true}
                                        onChange={(e) => handleSettingsChange('include_options_chain', e.target.checked)}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                                    />
                                }
                                label="Include Options Chain"
                            />
                        </Grid>
                    </Grid>
                );

            default:
                return null;
        }
    };

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view data quality configurations.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <VerifiedUserIcon sx={{ fontSize: 28, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                    Data Quality & Storage
                                </Typography>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                    Configure data quality filters, gap filling, retention policies, and replay engine
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    {canAdd && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleCreateOpen()}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                color: '#fff',
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: '12px',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.15)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            Add Configuration
                        </Button>
                    )}
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Success Alert */}
                {successMessage && (
                    <Alert severity="success" sx={{ mb: 3, borderRadius: '12px', fontFamily }} onClose={() => setSuccessMessage(null)}
                        icon={<CheckCircleIcon />}
                    >
                        {successMessage}
                    </Alert>
                )}

                {/* Configs Table */}
                <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 4 }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>Name</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>Type</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>Active</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>Settings</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>Last Run</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>Records Affected</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <CircularProgress sx={{ color: ACCENT }} />
                                        </TableCell>
                                    </TableRow>
                                ) : configs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <VerifiedUserIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontWeight: 500 }}>
                                                No data quality configurations found
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Create a configuration or use a preset template below
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    configs.map((config) => (
                                        <TableRow key={config.id} hover>
                                            <TableCell>
                                                <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                                    {config.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={DataQualityConfigTypeLabels[config.config_type] || config.config_type}
                                                    size="small"
                                                    color={DataQualityConfigTypeColors[config.config_type] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={config.is_active ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    sx={{
                                                        fontFamily,
                                                        fontWeight: 600,
                                                        bgcolor: config.is_active ? '#dcfce7' : '#fee2e2',
                                                        color: config.is_active ? '#16a34a' : '#dc2626',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontFamily,
                                                        color: '#64748b',
                                                        maxWidth: 200,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        fontSize: '0.8rem',
                                                    }}
                                                >
                                                    {config.settings && typeof config.settings === 'object'
                                                        ? JSON.stringify(config.settings)
                                                        : config.settings || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily, color: '#64748b' }}>
                                                    {config.last_run_at
                                                        ? new Date(config.last_run_at).toLocaleString()
                                                        : 'Never'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily, color: '#64748b' }}>
                                                    {config.records_affected != null ? config.records_affected.toLocaleString() : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                    <Tooltip title="Run Check">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRunCheck(config)}
                                                            disabled={runningId === config.id}
                                                            sx={{ color: '#10b981' }}
                                                        >
                                                            {runningId === config.id ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    {canEdit && (
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" onClick={() => handleEditOpen(config)} sx={{ color: '#64748b' }}>
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {canDelete && (
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(config)}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Preset Templates */}
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', mb: 2 }}>
                        Preset Templates
                    </Typography>
                    <Grid container spacing={2}>
                        {PRESET_TEMPLATES.map((template) => (
                            <Grid item xs={12} sm={6} md={3} key={template.config_type}>
                                <Paper
                                    sx={{
                                        p: 2.5,
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: 'none',
                                        transition: 'all 0.2s ease',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                            borderColor: template.color,
                                            boxShadow: `0 4px 20px ${template.color}20`,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                        <Box
                                            sx={{
                                                p: 1,
                                                borderRadius: '10px',
                                                bgcolor: `${template.color}15`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <VerifiedUserIcon sx={{ color: template.color, fontSize: 22 }} />
                                        </Box>
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                                            {template.name}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem', fontWeight: 500, mb: 2, flex: 1 }}>
                                        {template.description}
                                    </Typography>
                                    {canAdd && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => handleCreateOpen(template.config_type)}
                                            sx={{
                                                fontFamily,
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                borderColor: template.color,
                                                color: template.color,
                                                borderRadius: '10px',
                                                '&:hover': {
                                                    borderColor: template.color,
                                                    bgcolor: `${template.color}08`,
                                                },
                                            }}
                                        >
                                            Create
                                        </Button>
                                    )}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Box>

            {/* Add/Edit Dialog */}
            <Dialog open={formDialogOpen} onClose={handleFormClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {isEditing ? `Edit Configuration: ${selectedConfig?.name}` : 'Add Configuration'}
                </DialogTitle>
                <DialogContent>
                    {formErrors.submit && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{formErrors.submit}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Name"
                                value={formData.name}
                                onChange={handleFormChange('name')}
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth error={!!formErrors.config_type} required>
                                <InputLabel>Config Type</InputLabel>
                                <Select
                                    value={formData.config_type}
                                    label="Config Type"
                                    onChange={handleConfigTypeChange}
                                    disabled={isEditing}
                                >
                                    {Object.entries(DataQualityConfigType).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>
                                            {DataQualityConfigTypeLabels[value]}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {formErrors.config_type && (
                                    <Typography variant="caption" color="error">{formErrors.config_type}</Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                                    />
                                }
                                label="Active"
                            />
                        </Grid>

                        {/* Settings Section */}
                        {formData.config_type && (
                            <Grid item xs={12}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontFamily,
                                        fontWeight: 700,
                                        color: '#1e293b',
                                        mb: 1.5,
                                        pb: 1,
                                        borderBottom: '1px solid #e2e8f0',
                                    }}
                                >
                                    Settings - {DataQualityConfigTypeLabels[formData.config_type]}
                                </Typography>
                                {renderSettingsFields()}
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleFormClose} disabled={submitting} sx={{ fontFamily }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleFormSubmit}
                        disabled={submitting}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, fontWeight: 600 }}
                    >
                        {submitting ? <CircularProgress size={24} /> : isEditing ? 'Save Changes' : 'Create Configuration'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Configuration</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily }}>
                        Are you sure you want to delete this configuration?
                    </Typography>
                    {selectedConfig && (
                        <Typography sx={{ fontFamily, fontWeight: 600, mt: 1, color: '#1e293b' }}>
                            {selectedConfig.name}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontFamily }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm} sx={{ fontFamily, fontWeight: 600 }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DataQualityPage;
